import React, { useState, useMemo } from 'react'
import { View, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Screen, Text, Icon, SearchBar, FeatureTooltip, ContextualDropdown } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { useGetNearbyContacts, useGetNearbyVisibility, NearbyContact, useScreen } from '@hooks'
import { NearbyContactItem } from './components/NearbyContactItem'
import { VisibilityPanel } from './components/VisibilityPanel'

type Perimeter = 10 | 50 | 100
type ScopeFilter = 'PRIVATE' | 'PROFESSIONAL' | null

const LEFT_BUCKETS: Perimeter[] = [10, 50]
const RIGHT_BUCKET: Perimeter = 100

const filterContacts = (contacts: NearbyContact[], search: string) => {
  if (!search.trim()) return contacts
  const q = search.toLowerCase()
  return contacts.filter(
    (c) =>
      c.fullName.toLowerCase().includes(q) ||
      c.professions.some((p) => p.toLowerCase().includes(q)),
  )
}

const getGroup = (
  contacts: NearbyContact[],
  minExclusive: number,
  maxInclusive: number,
): NearbyContact[] =>
  contacts.filter((c) => c.distance != null && c.distance > minExclusive && c.distance <= maxInclusive)

export const NearbyScreen: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const navigation = useNavigation()
  const { isMobile } = useScreen()

  const [perimeter, setPerimeter] = useState<Perimeter>(100)
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>(null)
  const [showVisibilityPanel, setShowVisibilityPanel] = useState(false)
  const [searchText, setSearchText] = useState('')

  const { data, isLoading } = useGetNearbyContacts({
    radius: perimeter,
    mode: 'distance',
    scope: scopeFilter ?? undefined,
  })

  const { data: visibilityData } = useGetNearbyVisibility()

  const perimeterItems = useMemo(() => [
    { id: '10', name: t('nearby.perimeter10') },
    { id: '50', name: t('nearby.perimeter50') },
    { id: '100', name: t('nearby.perimeter100') },
  ], [t])

  const perimeterItemsMobile = useMemo(() => [
    { id: '10', name: t('nearby.perimeter10Short') },
    { id: '50', name: t('nearby.perimeter50Short') },
    { id: '100', name: t('nearby.perimeter100Short') },
  ], [t])

  const visibilityTexts: Record<string, string> = useMemo(() => ({
    ALL: t('nearby.visibilityAll'),
    PROFESSIONAL: t('nearby.visibilityProfessional'),
    PRIVATE: t('nearby.visibilityPrivate'),
    HIDDEN: t('nearby.visibilityHidden'),
  }), [t])

  const visibilityTextsMobile: Record<string, string> = useMemo(() => ({
    ALL: t('nearby.visibilityAllMobile'),
    PROFESSIONAL: t('nearby.visibilityProfessionalMobile'),
    PRIVATE: t('nearby.visibilityPrivateMobile'),
    HIDDEN: t('nearby.visibilityHiddenMobile'),
  }), [t])

  const allContacts = useMemo(
    () => filterContacts(data?.contacts ?? [], searchText),
    [data?.contacts, searchText],
  )

  const leftGroups = useMemo(() => {
    const activeBuckets = LEFT_BUCKETS.filter((b) => b <= perimeter)
    return activeBuckets.map((bucket, i) => {
      const prev = i === 0 ? 0 : LEFT_BUCKETS[i - 1]
      return { distance: bucket, contacts: getGroup(allContacts, prev, bucket) }
    })
  }, [allContacts, perimeter])

  const rightGroup = useMemo(() => {
    if (perimeter < RIGHT_BUCKET) return null
    const contacts = getGroup(allContacts, 50, RIGHT_BUCKET)
    return { distance: RIGHT_BUCKET, contacts }
  }, [allContacts, perimeter])

  const handleContactPress = (contactId: string) => {
    ;(navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contactId },
    })
  }

  const toggleScopeFilter = (scope: 'PRIVATE' | 'PROFESSIONAL') => {
    setScopeFilter((prev) => (prev === scope ? null : scope))
  }

  const visibilityText = isMobile
    ? visibilityTextsMobile[visibilityData?.scope ?? 'ALL']
    : visibilityTexts[visibilityData?.scope ?? 'ALL']

  return (
    <Screen>
      <View style={styles.outerRow}>

        {/* ── Main content (left side) ── */}
        <View style={styles.mainContent}>

          {/* Page header */}
          <View style={styles.pageHeader}>
            {!isMobile && <Text type="titleBigBold" style={styles.pageTitle}>
              {t('nearby.pageTitle')}
            </Text>}
            <View style={[styles.visibilityBanner, isMobile && styles.visibilityBannerMobile]}>
              <Text type="bodySmall" color={theme.colors.secondary} style={styles.visibilityBannerText}>
                {visibilityText ?? t('nearby.visibilityLoading')}
              </Text>
              <TouchableOpacity
                style={styles.visibilityButton}
                onPress={() => setShowVisibilityPanel((v) => !v)}
                activeOpacity={0.8}
              >
                <Text type="bodySmallBold" color={theme.colors.white}>
                  {t('nearby.editVisibility')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <FeatureTooltip
            id="nearby_intro"
            title={t('featureTooltip.nearby_intro.title')}
            description={t('featureTooltip.nearby_intro.description')}
            icon="mapPin"
          />

          {/* Search + Filters */}
          <View style={!isMobile && styles.controlsRow}>
            <View style={styles.searchRow}>
              <SearchBar
                value={searchText}
                onChangeText={setSearchText}
                containerStyle={!isMobile && styles.searchBar}
              />
            </View>

            {/* Filters row */}
            <View style={[styles.filtersRow, isMobile && styles.filtersRowMobile]}>
              {!isMobile && <ContextualDropdown
                data={perimeterItems}
                label={t('nearby.perimeterLabel')}
                selectedId={String(perimeter)}
                onSelect={(id) => setPerimeter(Number(id) as Perimeter)}
                buttonStyle={styles.perimeterDropdown}
              />}
              <TouchableOpacity
                style={[styles.filterPill, scopeFilter === 'PRIVATE' && { borderColor: theme.colors.green }]}
                onPress={() => toggleScopeFilter('PRIVATE')}
                activeOpacity={0.7}
              >
                <Icon name="lock" size={14} color={scopeFilter === 'PRIVATE' ? theme.colors.green : theme.colors.greyBlue} />
                <Text
                  type="bodySmall"
                  style={scopeFilter === 'PRIVATE' ? [styles.filterPillText, { color: theme.colors.green, fontWeight: '600' as const }] : styles.filterPillText}
                >
                  {t('nearby.filterPrivate')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.filterPill, scopeFilter === 'PROFESSIONAL' && { borderColor: theme.colors.primary }]}
                onPress={() => toggleScopeFilter('PROFESSIONAL')}
                activeOpacity={0.7}
              >
                <Icon name="work" size={14} color={scopeFilter === 'PROFESSIONAL' ? theme.colors.primary : theme.colors.greyBlue} />
                <Text
                  type="bodySmall"
                  style={scopeFilter === 'PROFESSIONAL' ? [styles.filterPillText, { color: theme.colors.primary, fontWeight: '600' as const }] : styles.filterPillText}
                >
                  {t('nearby.filterPro')}
                </Text>
              </TouchableOpacity>
              {isMobile && <ContextualDropdown
                data={perimeterItemsMobile}
                label={t('nearby.perimeterLabel')}
                selectedId={String(perimeter)}
                onSelect={(id) => setPerimeter(Number(id) as Perimeter)}
                buttonStyle={styles.filterPillFlex}
              />}
            </View>
          </View>

          {/* Content */}
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          ) : allContacts.length === 0 ? (
            <View style={styles.empty}>
              <Icon name="mapPin" size={36} color={theme.colors.greyBlue} />
              <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.emptyText}>
                {searchText ? t('nearby.emptyNoResults') : t('nearby.emptyNoContacts')}
              </Text>
            </View>
          ) : isMobile ? (
            /* Mobile: single column, all groups */
            <ScrollView style={styles.leftScrollArea}>
              {[...leftGroups, ...(rightGroup ? [rightGroup] : [])].map((group) =>
                group.contacts.length > 0 ? (
                  <View key={group.distance}>
                    <View style={styles.sectionHeader}>
                      <Text type="bodySmall" color={theme.colors.greyBlue}>
                        {t('nearby.groupLabel', { distance: group.distance })}
                      </Text>
                    </View>
                    {group.contacts.map((contact) => (
                      <NearbyContactItem
                        key={contact.id}
                        contact={contact}
                        onPress={() => handleContactPress(contact.id)}
                      />
                    ))}
                  </View>
                ) : null,
              )}
            </ScrollView>
          ) : (
            /* Desktop: two columns */
            <View style={styles.body}>
              <ScrollView style={styles.leftScrollArea}>
                {leftGroups.map((group) =>
                  group.contacts.length > 0 ? (
                    <View key={group.distance}>
                      <View style={styles.sectionHeader}>
                        <Text type="bodySmall" color={theme.colors.greyBlue}>
                          {t('nearby.groupLabel', { distance: group.distance })}
                        </Text>
                      </View>
                      {group.contacts.map((contact) => (
                        <NearbyContactItem
                          key={contact.id}
                          contact={contact}
                          onPress={() => handleContactPress(contact.id)}
                        />
                      ))}
                    </View>
                  ) : null,
                )}
              </ScrollView>
              <ScrollView style={styles.rightScrollArea}>
                {rightGroup && rightGroup.contacts.length > 0 && (
                  <View>
                    <View style={styles.sectionHeader}>
                      <Text type="bodySmall" color={theme.colors.greyBlue}>
                        {t('nearby.groupLabel', { distance: rightGroup.distance })}
                      </Text>
                    </View>
                    {rightGroup.contacts.map((contact) => (
                      <NearbyContactItem
                        key={contact.id}
                        contact={contact}
                        onPress={() => handleContactPress(contact.id)}
                      />
                    ))}
                  </View>
                )}
              </ScrollView>
            </View>
          )}

        </View>

        {/* ── Visibility panel (full height, right side) ── */}
        {showVisibilityPanel && (
          <View style={[styles.visibilityPanelContainer, isMobile && styles.visibilityPanelContainerMobile]}>
            <VisibilityPanel onClose={() => setShowVisibilityPanel(false)} />
          </View>
        )}

      </View>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    outerRow: {
      flex: 1,
      flexDirection: 'row',
    },
    mainContent: {
      flex: 1,
    },
    pageHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 20,
      paddingTop: 16,
      paddingBottom: 8,
      gap: theme.spacing.md,
    },
    pageTitle: {
      flexShrink: 0,
    },
    visibilityBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      flexShrink: 1,
      backgroundColor: theme.colors.sidebar,
      borderRadius: 16,
      paddingHorizontal: 16,
      paddingVertical: 10,
    },
    visibilityBannerText: {
      flexShrink: 1,
    },
    visibilityBannerMobile: {
      flex: 1
    },
    visibilityButton: {
      backgroundColor: theme.colors.primary,
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 9,
      flexShrink: 0,
    },
    visibilityPanelContainer: {
      width: 320,
      borderLeftWidth: 0.5,
      borderLeftColor: theme.colors.grey,
    },
    visibilityPanelContainerMobile: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: undefined,
      borderLeftWidth: 0,
      zIndex: 10,
    },
    controlsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    searchRow: {
      paddingHorizontal: 16,
      paddingVertical: 10,
    },
    searchBar: {
      width: 280,
    },
    filtersRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing.md,
      paddingHorizontal: 16,
    },
    filtersRowMobile: {
      justifyContent: 'flex-start',
    },
    filterPillFlex: {
      flex: 1,
      justifyContent: 'center',
    },
    body: {
      flex: 1,
      flexDirection: 'row',
    },
    leftScrollArea: {
      flex: 1,
      borderRightWidth: 0.5,
      borderRightColor: theme.colors.grey,
    },
    rightScrollArea: {
      flex: 1,
    },
    perimeterDropdown: {
      flex: 0,
      minWidth: 230,
    },
    filterPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: theme.colors.lightGrey,
    },
    filterPillText: {
      color: theme.colors.greyBlue,
    },
    sectionHeader: {
      paddingHorizontal: 16,
      paddingVertical: 10,
      backgroundColor: theme.colors.grey05,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 40,
    },
    empty: {
      alignItems: 'center',
      paddingVertical: 40,
      gap: 10,
    },
    emptyText: {
      textAlign: 'center',
    },
  })
